// (keep imports unchanged)
import { useState, useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Button from "react-bootstrap/Button";
import Form from "react-bootstrap/Form";
import { all_routes } from "feature-module/router/all_routes";
import { getExam, submitExam } from "utils/userThunk";
import { useDispatch } from "react-redux";
import toast from "react-hot-toast";
import { useSelector } from "react-redux";

const QuizTest = () => {
  const toggle=useSelector(store=>store.toggle);
  const [quiz, setQuiz] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [skipped, setSkipped] = useState([]);

  const [timeLeft, setTimeLeft] = useState(null);
  const [preExamTimeLeft, setPreExamTimeLeft] = useState(null);
  const [examStarted, setExamStarted] = useState(false);

  const [showWarning, setShowWarning] = useState(false);
  const [warningCount, setWarningCount] = useState(0);
  const [isQuizEnded, setIsQuizEnded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState("");

  const navigate = useNavigate();
  const timerRef = useRef(null);
  const dispatch = useDispatch();
  const location = useLocation();

  // Store exam window (ms) so we can compute remaining time at transitions
  const examWindowRef = useRef({ start: null, end: null });

  // Refs used by global listeners to avoid stale closures
  const examActiveRef = useRef(false);
  const warningCountRef = useRef(0);
  const lastWarnAtRef = useRef(0);

  const [currentPage, setCurrentPage] = useState(0);
  const questionsPerPage = 18;
  const totalPages = Math.ceil(questions.length / questionsPerPage);
  const startIndex = currentPage * questionsPerPage;
  const currentQuestions = questions.slice(
    startIndex,
    startIndex + questionsPerPage
  );

  useEffect(() => {
    setCurrentPage(Math.floor(currentQuestionIndex / questionsPerPage));
  }, [currentQuestionIndex]);

  const queryParams = new URLSearchParams(location.search);
  const language = queryParams.get("language");
  const examType = queryParams.get("examType");

  const maxWarnings = 3;
  const selectedLanguage =
    localStorage.getItem("selectedLanguage") || "english";
  const userId = localStorage.getItem("_id");

  // Keep refs in sync
  useEffect(() => {
    examActiveRef.current = examStarted && !isQuizEnded;
  }, [examStarted, isQuizEnded]);

  useEffect(() => {
    warningCountRef.current = warningCount;
  }, [warningCount]);

  // Fetch exam + timers
  useEffect(() => {
    const fetchQuizData = async () => {
      try {
        setLoading(true);
        const result = await dispatch(getExam({ language, examType, userId }));

        if (getExam.fulfilled.match(result)) {
          const exam = result?.payload?.exam || {};

          setMessage(result?.payload?.message);
          setQuiz(exam);
          const filteredQuestions =
            result?.payload?.paper?.filter(
              (q) => q.language === selectedLanguage
            ) || [];
          setQuestions(filteredQuestions);

          const start = new Date(
            `${exam.examDate}T${exam.examTime}:00`
          ).getTime();
          const end = start + Number(exam.duration) * 60 * 1000;
          examWindowRef.current = { start, end };

          const now = Date.now();
          if (now < start) {
            setPreExamTimeLeft(Math.floor((start - now) / 1000));
            setExamStarted(false);
            setTimeLeft(null);
          } else {
            const diff = Math.max(0, Math.floor((end - now) / 1000));
            setTimeLeft(diff);
            setExamStarted(true);
          }
        } else {
          setMessage(result?.payload);
        }
      } catch (e) {
        console.error(e);
        toast.error("Failed to load exam.");
      } finally {
        setLoading(false);
      }
    };
    fetchQuizData();
  }, [selectedLanguage, language, examType]);
 

  // Exam countdown
  useEffect(() => {
    if (timeLeft === null || isQuizEnded) return;
    if (timeLeft <= 0) {
      handleSubmitQuiz();
      return;
    }
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setTimeLeft((p) => p - 1), 1000);
    return () => clearInterval(timerRef.current);
  }, [timeLeft, isQuizEnded,toggle.value]);
  useEffect(() => {
    if(toggle.value){
      handleSubmitQuiz();
    }
  }, [toggle.value]);

  // Pre-exam countdown => start exam/timer at 0
  useEffect(() => {
    if (preExamTimeLeft === null || examStarted) return;

    if (preExamTimeLeft <= 0) {
      const remaining = Math.max(
        0,
        Math.floor((examWindowRef.current.end - Date.now()) / 1000)
      );
      setTimeLeft(remaining);
      setExamStarted(true);
      return;
    }

    const id = setInterval(() => setPreExamTimeLeft((p) => p - 1), 1000);
    return () => clearInterval(id);
  }, [preExamTimeLeft, examStarted]);

  // Robust warning listeners + throttle
  useEffect(() => {
    const THROTTLE_MS = 1200;

    const bumpWarning = () => {
      if (!examActiveRef.current) return;

      const now = Date.now();
      if (now - lastWarnAtRef.current < THROTTLE_MS) return; // prevent double fire
      lastWarnAtRef.current = now;

      // Increment using ref to avoid stale state
      const next = Math.min(warningCountRef.current + 1, maxWarnings);
      warningCountRef.current = next;
      setWarningCount(next);
      setShowWarning(true);

      if (next >= maxWarnings) {
        // Auto-submit immediately at max
        handleSubmitQuiz();
      }
    };

    const onVisibility = () => {
      if (document.hidden) bumpWarning();
    };
    const onBlur = () => bumpWarning();

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("blur", onBlur);
    return () => {
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("blur", onBlur);
    };
  }, [maxWarnings,quiz]); // static handlers using refs

  const formatTime = (seconds) => {
    const sec = Math.max(0, Number(seconds || 0));
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    return `${h.toString().padStart(2, "0")}:${m
      .toString()
      .padStart(2, "0")}:${s.toString().padStart(2, "0")}`;
  };

  const handleAnswerSelect = (questionId, answerIndex, isMultiSelect) => {
    setAnswers((prev) => {
      const updated = { ...prev };
      const existing = updated[questionId] || [];
      updated[questionId] = isMultiSelect
        ? existing.includes(answerIndex)
          ? existing.filter((i) => i !== answerIndex)
          : [...existing, answerIndex]
        : [answerIndex];
      return updated;
    });
    setSkipped((prev) => prev.filter((i) => i !== currentQuestionIndex));
  };

  const handleSkip = () => {
    const questionId = questions[currentQuestionIndex]?._id;
    if (!questionId) return;
    if (!skipped.includes(currentQuestionIndex)) {
      setSkipped((prev) => [...prev, currentQuestionIndex]);
    }
    setAnswers((prev) => {
      const updated = { ...prev };
      delete updated[questionId];
      return updated;
    });
    setCurrentQuestionIndex((prev) =>
      prev < questions.length - 1 ? prev + 1 : prev
    );
  };

  const generateSubmissionSheet = () =>
    questions.map((q) => {
      const selectedIndices = answers[q._id] || [];
      const selectedTexts =
        q.type === "truefalse"
          ? selectedIndices.map((i) => (i === 0 ? "True" : "False"))
          : selectedIndices.map((i) => q.answers[i]?.text).filter(Boolean);
      return {
        questionId: q._id,
        selectedOptions: selectedTexts.length > 0 ? selectedTexts : [],
      };
    });

  const handleSubmitQuiz = async () => {
    if (timerRef.current) clearInterval(timerRef.current);
    if (isQuizEnded) return;
    setIsQuizEnded(true);
    // console.log("quiz--------> ", quiz);
    try {
      const submissionSheet = generateSubmissionSheet();
      let result;
      if (quiz) {
        result = await dispatch(
          submitExam({
            userId,
            examId: quiz?._id,
            language: selectedLanguage,
            submissionSheet,
          })
        );
        if (submitExam.fulfilled.match(result)) {
        navigate(all_routes.studentQuizResult, {
          state: {
            attemptedCount: result?.payload?.attemptedQuestions || 0,
            declarationDate: result?.payload?.resultDate || "2025-06-31",
          },
        });
      } else {
        throw new Error(result.payload?.message || "Failed to submit exam");
      }
      }

      
    } catch (e) {
      console.error(e);
      toast.error("Something went wrong!");
    }
  };

  const handleQuitQuiz = async () => {
    await handleSubmitQuiz();
    window.close();

    setTimeout(() => {
      if (!window.closed) window.history.back();
    }, 300);
  };

  const acknowledgeWarning = () => setShowWarning(false);

  const currentQuestion = questions[currentQuestionIndex];

  if (loading) {
    return (
      <div
        style={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>Loading quiz...</h2>
      </div>
    );
  }

  if (!quiz || !questions.length) {
    return (
      <div
        style={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <h2>{message || "No exam started yet."}</h2>
      </div>
    );
  }

  if (!examStarted && preExamTimeLeft !== null) {
    return (
      <div
        style={{
          height: "70vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
        }}
      >
        <h2>Exam starting in {" "}
          {new Date(preExamTimeLeft * 1000).toISOString().substr(11, 8)}{" "}
         minutes.</h2>
        
      </div>
    );
  }

  return (
    <div className="quiz-test-container py-4">
      <div className="container">
        <div className="row">
          {/* Left Panel */}
          <div className="col-md-8 mb-4">
            <div className="card p-4">
              <div className="d-flex justify-content-between align-items-center mb-4">
                <h3>{quiz.description}</h3>
                <div
                  className={` fw-bold d-md-none d-block ${
                    timeLeft < 300
                      ? "text-danger"
                      : timeLeft < 900
                      ? "text-warning"
                      : "text-success"
                  }`}
                >
                  Time Left: {formatTime(timeLeft)}
                </div>
              </div>
              <div className="question-card">
                <p>
                  Question {currentQuestionIndex + 1} of {questions.length}
                </p>
                <div
                  dangerouslySetInnerHTML={{
                    __html: currentQuestion.quizQuestion,
                  }}
                  style={{ marginBottom: "20px" }}
                />
                <Form>
                  {["MCQ", "multiple"].includes(currentQuestion.type) ? (
                    <div className="answers">
                      {currentQuestion.answers.map((answer, index) => {
                        const inputId = `answer-${currentQuestion._id}-${index}`;
                        return (
                          <Form.Check
                            key={index}
                            type="checkbox"
                            id={inputId}
                            name={`mcq-${currentQuestion._id}`}
                            label={
                              <span style={{ cursor: "pointer" }}>
                                {answer.text}
                              </span>
                            }
                            checked={(
                              answers[currentQuestion._id] || []
                            ).includes(index)}
                            onChange={() =>
                              handleAnswerSelect(
                                currentQuestion._id,
                                index,
                                true
                              )
                            }
                            disabled={isQuizEnded}
                            className="mb-2"
                          />
                        );
                      })}
                    </div>
                  ) : currentQuestion.type === "truefalse" ? (
                    <div className="answers">
                      {["True", "False"].map((val, idx) => {
                        const inputId = `truefalse-${currentQuestion._id}-${idx}`;
                        return (
                          <Form.Check
                            key={idx}
                            type="radio"
                            id={inputId}
                            name={`truefalse-${currentQuestion._id}`}
                            label={
                              <span style={{ cursor: "pointer" }}>{val}</span>
                            }
                            checked={(
                              answers[currentQuestion._id] || []
                            ).includes(idx)}
                            onChange={() =>
                              handleAnswerSelect(
                                currentQuestion._id,
                                idx,
                                false
                              )
                            }
                            disabled={isQuizEnded}
                            className="mb-2"
                          />
                        );
                      })}
                    </div>
                  ) : (
                    <div className="answers">
                      {currentQuestion.answers.map((answer, index) => {
                        const inputId = `single-${currentQuestion._id}-${index}`;
                        return (
                          <Form.Check
                            key={index}
                            type="radio"
                            id={inputId}
                            name={`single-${currentQuestion._id}`}
                            label={
                              <span style={{ cursor: "pointer" }}>
                                {answer.text}
                              </span>
                            }
                            checked={(
                              answers[currentQuestion._id] || []
                            ).includes(index)}
                            onChange={() =>
                              handleAnswerSelect(
                                currentQuestion._id,
                                index,
                                false
                              )
                            }
                            disabled={isQuizEnded}
                            className="mb-2"
                          />
                        );
                      })}
                    </div>
                  )}
                </Form>
              </div>

              <div className="d-flex justify-content-between mt-4">
                <Button
                  variant="secondary"
                  disabled={currentQuestionIndex === 0}
                  onClick={() => setCurrentQuestionIndex((prev) => prev - 1)}
                >
                  Previous
                </Button>
                <div className="d-flex gap-2">
                  <Button
                    variant="warning"
                    onClick={handleSkip}
                    disabled={isQuizEnded}
                  >
                    Skip
                  </Button>
                  {currentQuestionIndex < questions.length - 1 ? (
                    <Button
                      variant="primary"
                      onClick={() =>
                        setCurrentQuestionIndex((prev) => prev + 1)
                      }
                      disabled={!answers[currentQuestion._id]?.length}
                    >
                      Next
                    </Button>
                  ) : (
                    <Button
                      variant="success"
                      onClick={handleSubmitQuiz}
                      disabled={isQuizEnded}
                    >
                      Submit Quiz
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Right Panel */}
          <div className="col-md-4">
            <div className="card p-3">
              <div className="d-flex justify-content-between">
                <h5 className="mb-3 text-center">Time Left</h5>
                <div
                  className={`text-center fw-bold mb-3 fs-3 ${
                    timeLeft < 300
                      ? "text-danger"
                      : timeLeft < 900
                      ? "text-warning"
                      : "text-success"
                  }`}
                >
                  {formatTime(timeLeft)}
                </div>
              </div>

              <p
                style={{
                  fontSize: "12px",
                  marginBottom: "15px",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <span
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#ccc",
                    marginRight: 4,
                  }}
                ></span>{" "}
                Not attempted &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <span
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#4CAF50",
                    marginRight: 4,
                  }}
                ></span>{" "}
                Answered &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                <span
                  style={{
                    display: "inline-block",
                    width: 16,
                    height: 16,
                    borderRadius: "50%",
                    backgroundColor: "#FFC107",
                    marginRight: 4,
                  }}
                ></span>{" "}
                Skipped
              </p>

              <h6 className="mb-2">Question Palette</h6>
              <div>
                <div className="d-flex flex-wrap gap-2 justify-content-center">
                  {currentQuestions.map((_, index) => {
                    const realIndex = startIndex + index;
                    let bgColor = "#ccc";
                    if (answers[questions[realIndex]._id]) bgColor = "#28a745";
                    if (skipped.includes(realIndex)) bgColor = "#ffc107";
                    return (
                      <button
                        key={realIndex}
                        className="btn btn-sm text-white"
                        style={{
                          width: 50,
                          height: 50,
                          backgroundColor: bgColor,
                          borderRadius: 4,
                          padding: 0,
                          fontSize: 15,
                        }}
                        disabled={bgColor === "#ccc"}
                        onClick={() => setCurrentQuestionIndex(realIndex)}
                      >
                        {realIndex + 1}
                      </button>
                    );
                  })}
                </div>

                <div className="d-flex justify-content-center gap-3 mt-3">
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      setCurrentPage((prev) => Math.max(prev - 1, 0))
                    }
                    disabled={currentPage === 0}
                  >
                    Previous
                  </button>
                  <span className="align-self-center">
                    Page {currentPage + 1} of {totalPages}
                  </span>
                  <button
                    className="btn btn-outline-primary btn-sm"
                    onClick={() =>
                      setCurrentPage((prev) =>
                        Math.min(prev + 1, totalPages - 1)
                      )
                    }
                    disabled={currentPage === totalPages - 1}
                  >
                    Next
                  </button>
                </div>
              </div>

              <div className="d-flex justify-content-between mt-3">
                <Button
                  variant="warning"
                  onClick={handleQuitQuiz}
                  disabled={isQuizEnded}
                >
                  Quit
                </Button>
                <Button
                  variant="primary"
                  onClick={handleSubmitQuiz}
                  disabled={isQuizEnded}
                >
                  Submit Quiz
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fixed overlay Warning Popup */}
      {showWarning && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.45)",
            zIndex: 1050,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 16,
          }}
          role="dialog"
          aria-modal="true"
          onClick={acknowledgeWarning}
        >
          <div
            className="card"
            style={{ maxWidth: 520, width: "100%" }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="card-header">
              <strong>
                Warning {warningCount}/{maxWarnings}
              </strong>
            </div>
            <div className="card-body">
              Please don’t switch tabs or minimize during the exam. Repeated
              violations will auto-submit your test.
            </div>
            <div className="card-footer d-flex justify-content-end gap-2">
              <Button variant="primary" onClick={acknowledgeWarning}>
                OK
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuizTest;
